import type { ElasticSearchHistogramResponse, PCAReportMetric } from 'c-sdk';
import { assocPath } from 'ramda';
import { esValuesToPcaValues } from './Transformers';

const MULTI_KEY_SEPARATOR = ' | ';

const flattenMultiField = (
    node: any,
    depth: number,
    maxDepth: number,
    parts: string[],
    out: Array<[string, any]>,
) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return;
    if (depth >= maxDepth) {
        out.push([parts.join(MULTI_KEY_SEPARATOR), node]);
        return;
    }
    Object.entries(node).forEach(([k, v]) => {
        flattenMultiField(v, depth + 1, maxDepth, [...parts, String(k)], out);
    });
};

/**
 *
 * Hourly example
 * {
	"Global D48": {
	    // dates
		"2021-07-05": {
		    // hours
			"0": [
				140,
				0,
				"",
				0,
				0,
				140
			],
		}
	}
}
 */
export const hourlyHistogramToOldDataStructure = ({
    metric,
    data,
    groupByField = true,
    onlyHourNumber = true,
    fieldCount,
}: {
    metric: PCAReportMetric;
    data: ElasticSearchHistogramResponse;
    groupByField?: boolean;
    onlyHourNumber?: boolean;
    fieldCount?: number;
}) =>
    Object.entries(data?.data ?? {}).reduce((acc, [dateKey, byField]) => {
        // just getting the date portion (ignoring hours) to avoid localisation
        // since the date string is `yyyy-MM-dd HH:mm`
        const dateString = dateKey.split(' ')[0];
        // second portion is the time `HH:mm`
        let hourString = dateKey.split(' ')[1] ?? '';

        // if `10:00`, it'll become `10`
        if (onlyHourNumber) hourString = String(Number(hourString.split(':')[0]));

        if (fieldCount && fieldCount > 1) {
            const leaves: Array<[string, any]> = [];
            flattenMultiField(byField, 0, fieldCount, [], leaves);
            leaves.forEach(([composite, fieldState]) => {
                const path = groupByField
                    ? [composite, dateString, hourString]
                    : [dateString, hourString];
                // eslint-disable-next-line no-param-reassign
                acc = assocPath(path, esValuesToPcaValues(metric, fieldState as any), acc);
            });
            return acc;
        }

        Object.entries(byField).forEach(([fieldName, fieldState]) => {
            const path = groupByField
                ? [fieldName, dateString, hourString]
                : [dateString, hourString];

            // eslint-disable-next-line no-param-reassign
            acc = assocPath(path, esValuesToPcaValues(metric, fieldState), acc);
        });
        return acc;
    }, {});

/**
 *
 * Daily example
 *
 {
	"Global D48": {
		"2021-07-05": [
			140,
			0,
			"",
			0,
			0,
			140
		]
	}
}
 */
export const dailyHistogramToOldDataStructure = ({
    metric,
    data,
    groupByField = true,
    fieldCount,
}: {
    metric: PCAReportMetric;
    data: ElasticSearchHistogramResponse;
    groupByField?: boolean;
    fieldCount?: number;
}) =>
    Object.entries(data?.data ?? {}).reduce((acc, [dateKey, byField]) => {
        // just getting the date portion (ignoring hours) to avoid localisation
        // since the date string is `yyyy-MM-dd HH:mm`
        const dateString = dateKey.split(' ')[0];

        if (fieldCount && fieldCount > 1) {
            const leaves: Array<[string, any]> = [];
            flattenMultiField(byField, 0, fieldCount, [], leaves);
            leaves.forEach(([composite, fieldState]) => {
                const path = groupByField ? [composite, dateString] : [dateString];
                // eslint-disable-next-line no-param-reassign
                acc = assocPath(path, esValuesToPcaValues(metric, fieldState as any), acc);
            });
            return acc;
        }

        Object.entries(byField).forEach(([fieldName, fieldState]) => {
            const path = groupByField ? [fieldName, dateString] : [dateString];
            // eslint-disable-next-line no-param-reassign
            acc = assocPath(path, esValuesToPcaValues(metric, fieldState), acc);
        });
        return acc;
    }, {});
